import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRing, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

// 72h cooldown — matches NUDGE_COOLDOWN_HOURS in backend/routes/nudge.py
export const NUDGE_COOLDOWN_HOURS = 72;
export const nudgeCooldownInfo = (lastNudgedAt) => {
  if (!lastNudgedAt) return { inCooldown: false, hoursAgo: null };
  const hoursAgo = Math.floor((Date.now() - new Date(lastNudgedAt).getTime()) / 36e5);
  return { inCooldown: hoursAgo >= 0 && hoursAgo < NUDGE_COOLDOWN_HOURS, hoursAgo };
};

export const NudgeClientDialog = ({
  open, onOpenChange, project, itemType, itemId, itemTitle, darkMode, onSent,
}) => {
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const clientName = project?.client?.name || 'Client';
  const clientEmail = (project?.client?.email || '').trim();

  const handleSend = async () => {
    if (!itemId) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/projects/${project.id}/nudge`, {
        item_type: itemType,
        item_id: itemId,
        custom_note: note.trim() || null,
      });
      toast.success(res.data.message || 'Nudge sent');
      setNote('');
      onOpenChange(false);
      if (onSent) onSent(res.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send nudge');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-md ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
        <DialogHeader>
          <DialogTitle className={darkMode ? 'text-white' : ''}>
            <BellRing className="w-4 h-4 inline mr-2 text-[#BC5D41]" />
            Nudge client
          </DialogTitle>
          <DialogDescription>
            Sends a short, friendly email reminder about &ldquo;{itemTitle}&rdquo;.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-4">
          {clientEmail ? (
            <div
              className={`p-3 rounded-lg border text-sm ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-gray-300' : 'bg-gray-50 border-gray-200'}`}
              data-testid="nudge-recipient"
            >
              To: <span className="font-medium">{clientName}</span> &lt;{clientEmail}&gt;
            </div>
          ) : (
            <div
              className="p-3 rounded-lg border bg-amber-50 border-amber-200 text-sm text-amber-700 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-400"
              data-testid="nudge-no-email"
            >
              No client email on this project. Add one in Project Info first.
            </div>
          )}

          <div>
            <Label className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Personal note (optional)
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="e.g. No rush — happy to walk you through it on a quick call."
              className={`mt-1 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-white' : ''}`}
              data-testid="nudge-note-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!clientEmail || sending}
            className="bg-[#BC5D41] hover:bg-[#70170F] text-white"
            data-testid="nudge-send-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {sending ? 'Sending…' : 'Send Reminder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NudgeClientDialog;
